import { useState, useRef, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, Spinner } from '@ui';
import { FolderUpload, ReceiptIcon, X } from '@ui/icons';
import { cn } from '@shared/lib';
import { t } from '@shared/i18n';
import { heicPreviewBlob } from '@shared/lib/heicPreview';
import { toast } from '@shared/lib/toast';

export interface ReceiptUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentFile: File | null;
  /** An existing attachment is being fetched (lazily, only while this modal is open) and hasn't
   *  arrived yet — show a loading state instead of the empty drop-zone so it doesn't look like
   *  there's no attachment / invite picking a new file while the real one is still loading. */
  isLoadingExisting?: boolean;
  onUpload: (file: File | null) => void;
}

/**
 * Client-side receipt size cap. The backend refuses larger uploads, but a bill whose receipt upload
 * failed was still saved — and then showed "Receipt could not be loaded" on edit. Refusing the file
 * at pick time (with a message naming the limit) keeps an over-sized receipt out of the save
 * entirely. Keep in sync with `t.newBill.validation.receiptTooLarge`.
 */
export const MAX_RECEIPT_MB = 10;
export const MAX_RECEIPT_BYTES = MAX_RECEIPT_MB * 1024 * 1024;

const ALLOWED_EXTENSIONS = ['.heic', '.heif', '.jpg', '.jpeg', '.png', '.pdf'];
const ALLOWED_MIME_TYPES = [
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'application/pdf',
];

export function ReceiptUploadModal({
  open,
  onOpenChange,
  currentFile,
  isLoadingExisting = false,
  onUpload,
}: ReceiptUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(currentFile);
  // True once the user has picked or removed a file THIS session — stops the sync effect below from
  // clobbering that choice if the existing-attachment fetch (lazy, started on open) resolves after.
  const userChangedRef = useRef(false);
  // The existing attachment is now fetched lazily (only while this modal is open), so on first open
  // `currentFile` can still be null while the fetch is in flight — re-sync once it lands, unless the
  // user has already picked/removed a file in the meantime (`userChangedRef`).
  useEffect(() => {
    if (userChangedRef.current) return;
    setSelectedFile(currentFile);
  }, [currentFile]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // The <img> failed to decode whatever URL it was given — show the file-icon fallback instead of
  // a broken image. Reset on every new file pick/removal.
  const [previewFailed, setPreviewFailed] = useState(false);
  // A HEIC/HEIF preview conversion is running — the file card shows a "Preparing preview…" hint.
  const [isConverting, setIsConverting] = useState(false);

  // Build (and revoke) the image preview object URL from the selected file INSIDE an effect — not a
  // useState initializer — so it's StrictMode-safe. StrictMode double-invokes mount→cleanup→mount; an
  // initializer-created URL would get revoked by the cleanup yet linger (revoked) in state → a broken
  // <img> on reopen. Creating it here means the second mount makes a fresh, valid URL each time.
  //
  // HEIC/HEIF: browsers other than Safari cannot decode them in an <img>, so the raw object URL
  // rendered as a broken image. Convert to JPEG for the PREVIEW only (the original file is what
  // uploads) via `heicPreviewBlob` — a real-wasm libheif decode, cached per file so reopening the
  // modal (it remounts per open) shows the preview instantly instead of re-decoding. A
  // "Preparing preview…" hint shows while converting; failure keeps the file-icon fallback.
  useEffect(() => {
    let url: string | null = null;
    const lifecycle = { cancelled: false };
    const ext = selectedFile ? `.${selectedFile.name.split('.').pop()?.toLowerCase() ?? ''}` : '';
    const mime = selectedFile?.type.toLowerCase() ?? '';
    const isPDF = mime === 'application/pdf' || ext === '.pdf';
    const isHeic =
      mime === 'image/heic' || mime === 'image/heif' || ext === '.heic' || ext === '.heif';
    if (selectedFile && !isPDF && !isHeic) url = URL.createObjectURL(selectedFile);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- object-URL lifecycle needs an effect
    setPreviewUrl(url);
    setIsConverting(Boolean(selectedFile && isHeic));
    if (selectedFile && isHeic) {
      const file = selectedFile;
      void (async () => {
        try {
          const converted = await heicPreviewBlob(file);
          const convertedUrl = URL.createObjectURL(converted);
          if (lifecycle.cancelled) {
            URL.revokeObjectURL(convertedUrl);
            return;
          }
          url = convertedUrl;
          setPreviewUrl(convertedUrl);
        } catch {
          // Conversion failed (corrupt file, codec unavailable) — keep the file-icon fallback.
        } finally {
          if (!lifecycle.cancelled) setIsConverting(false);
        }
      })();
    }
    return () => {
      lifecycle.cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedFile]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelection(file);
      }
    }
  };

  const handleFileSelection = (file: File) => {
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    const mime = file.type.toLowerCase();

    const isWebp = ext === '.webp' || mime === 'image/webp';
    const isValid =
      !isWebp && (ALLOWED_MIME_TYPES.includes(mime) || ALLOWED_EXTENSIONS.includes(ext));
    if (!isValid) {
      toast.error(
        'Supported file formats: .heic, .heif, .jpg, .jpeg, .png, and .pdf. Please upload a file in one of these formats.',
      );
      return;
    }
    // Both entry points (browse + drag-drop) land here, so the size cap covers each. The input is
    // cleared so re-picking the same (still too large) file fires the change event again.
    if (file.size > MAX_RECEIPT_BYTES) {
      toast.error(t.newBill.validation.receiptTooLarge);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    userChangedRef.current = true;
    setPreviewFailed(false);
    setSelectedFile(file); // the effect (re)builds the preview URL
  };

  const handleRemoveFile = () => {
    userChangedRef.current = true;
    setPreviewFailed(false);
    setSelectedFile(null); // the effect clears + revokes the preview URL
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-60 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-125 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none',
          )}
        >
          {/* Header */}
          <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2">
            <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
              Attach Receipt
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                id="receipt-close-btn"
                type="button"
                aria-label="Close"
                className="flex size-7 cursor-pointer items-center justify-center rounded-full bg-border-soft hover:bg-border focus-visible:outline-none"
              >
                <X className="size-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="px-5 py-6">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'flex min-h-55 flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-6 text-center transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : selectedFile
                  ? 'border-border bg-surface'
                  : 'border-divider hover:border-primary/45',
              )}
            >
              {!selectedFile && isLoadingExisting ? (
                <>
                  <Spinner className="size-8 text-primary shrink-0" />
                  <span className="text-sm font-medium text-fg">{t.newBill.attachmentLoading}</span>
                </>
              ) : !selectedFile ? (
                <>
                  <FolderUpload className="size-12 text-primary shrink-0" />
                  <span className="text-sm font-medium text-fg">Drag your receipt to upload</span>
                  <div className="flex items-center gap-3 w-full max-w-50 text-muted-soft text-xs select-none">
                    <div className="h-px flex-1 bg-divider" />
                    <span>OR</span>
                    <div className="h-px flex-1 bg-divider" />
                  </div>
                  <Button type="button" variant="soft" size="md" onClick={handleBrowseClick}>
                    Browse files
                  </Button>
                </>
              ) : previewUrl && !previewFailed ? (
                <div className="relative flex flex-col items-center gap-3 w-full">
                  <div className="relative group max-w-full">
                    <img
                      src={previewUrl}
                      alt="Receipt preview"
                      onError={() => setPreviewFailed(true)}
                      className="max-h-40 max-w-full object-contain rounded-lg border border-divider shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile();
                      }}
                      className="absolute -top-2 -right-2 flex size-6 cursor-pointer items-center justify-center rounded-full bg-danger text-danger-fg shadow hover:opacity-90 focus-visible:outline-none"
                      title="Remove receipt"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 w-full">
                  <div className="relative flex size-20 items-center justify-center rounded-xl bg-accent-soft text-accent-text">
                    <ReceiptIcon className="size-10" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile();
                      }}
                      className="absolute -top-2 -right-2 flex size-6 cursor-pointer items-center justify-center rounded-full bg-danger text-danger-fg shadow hover:opacity-90 focus-visible:outline-none"
                      title="Remove receipt"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                  {isConverting ? (
                    <p className="flex items-center justify-center gap-1.5 text-xs text-muted-soft">
                      <Spinner className="size-3.5" />
                      {t.newBill.receiptPreviewPreparing}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".heic,.heif,.jpg,.jpeg,.png,.pdf,image/heic,image/heif,image/jpeg,image/png,application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileSelection(file);
                }
                e.target.value = '';
              }}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2.5 border-t border-border-soft bg-surface-raised px-5 py-3">
            <Button
              variant="primary"
              size="md"
              // Blocked while the existing attachment is still loading and nothing's been picked yet
              // — committing `null` here would wipe a real attachment the user never touched, just
              // because they clicked before the (now-lazy) fetch finished.
              disabled={!selectedFile && isLoadingExisting}
              onClick={() => {
                onUpload(selectedFile);
                onOpenChange(false);
              }}
            >
              Upload
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                onUpload(null);
                onOpenChange(false);
              }}
            >
              {t.newBill.cancel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
